import { Injectable } from "@angular/core";
import { MatStepper } from "@angular/material/stepper";

/** Keeps the component's `[linear]` binding in sync while it is temporarily suspended. */
export type LinearSync = (linear: boolean) => void;

/**
 * Shared stepper navigation for the assessment modules.
 *
 * Every assessment screen drives a `mat-horizontal-stepper` and persists the active step
 * under the `calculationStep` key, which import/export reads to enable its buttons.
 */
@Injectable({ providedIn: "root" })
export class StepperNavigationService {
  private static readonly STEP_KEY = "calculationStep";

  /** Step index outside the valid range, used to gray out export/save on the home screen. */
  static readonly NO_STEP = 9999;

  get currentStep(): number {
    return parseInt(localStorage.getItem(StepperNavigationService.STEP_KEY), 10) || 0;
  }

  setStep(index: number): void {
    localStorage.setItem(StepperNavigationService.STEP_KEY, index.toString());
  }

  /** Applicability is step zero on every assessment module. */
  resetToApplicability(): void {
    this.setStep(0);
  }

  /**
   * Advances one step, still honouring the stepper's linear validation.
   * `persist` mirrors the existing rule of only recording progress once the
   * applicability or Type A checkbox has been accepted.
   */
  next(stepper: MatStepper, persist: boolean = true): void {
    if (!stepper) {
      return;
    }
    if (persist) {
      this.setStep(stepper.selectedIndex + 1);
    }
    stepper.next();
  }

  /**
   * Moves back one step. Material re-validates every earlier step on any move, so a step
   * that is never marked complete would otherwise trap the user; going back always bypasses.
   */
  previous(stepper: MatStepper, persist: boolean = true, syncLinear?: LinearSync): void {
    if (!stepper) {
      return;
    }
    this.goToIndex(stepper, stepper.selectedIndex - 1, syncLinear, persist);
  }

  /**
   * Jumps to an arbitrary step. Angular Material refuses to move to a step when any earlier
   * step is incomplete, so linear mode is suspended for the assignment and restored on the
   * next microtask.
   */
  goToIndex(
    stepper: MatStepper,
    targetIndex: number,
    syncLinear?: LinearSync,
    persist: boolean = true
  ): void {
    if (!stepper || targetIndex < 0) {
      return;
    }

    const previousLinear = stepper.linear;
    stepper.linear = false;
    syncLinear?.(false);

    stepper.selectedIndex = targetIndex;

    Promise.resolve().then(() => {
      stepper.linear = previousLinear;
      syncLinear?.(previousLinear);
    });

    if (persist) {
      this.setStep(targetIndex);
    }
  }

  /** Convenience wrapper for skipping forward over steps Material will not mark complete. */
  skipForward(
    stepper: MatStepper,
    stepsToSkip: number,
    syncLinear?: LinearSync
  ): void {
    if (!stepper) {
      return;
    }
    this.goToIndex(stepper, stepper.selectedIndex + stepsToSkip, syncLinear);
  }
}
